"use client";

import { motion } from "framer-motion";
import {
  Clock,
  CalendarDays,
  Wallet,
  Target,
  Users,
  ShieldCheck,
} from "lucide-react";
import { fadeUp } from "@/hooks/useScrollReveal";

const FEATURES = [
  {
    icon: Clock,
    title: "Attendance that just works",
    desc: "Check-ins, shifts, and regularization — synced in real time.",
  },
  {
    icon: Wallet,
    title: "Payroll you can actually trust",
    desc: "Automated structures, deductions, and payslips built to survive an audit.",
  },
  {
    icon: CalendarDays,
    title: "Leave, without the back-and-forth",
    desc: "Approve from a phone in one tap.",
  },
  {
    icon: Users,
    title: "One directory, every team",
    desc: "A single source of truth for org structure and reporting lines.",
  },
  {
    icon: Target,
    title: "Reviews that finish on time",
    desc: "Goals and appraisal cycles with built-in reminders.",
  },
  {
    icon: ShieldCheck,
    title: "Access that respects your org chart",
    desc: "Role-based permissions, enforced end to end.",
  },
];

export default function Features() {
  return (
    <section id="features" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="max-w-2xl mb-16"
        >
          <span className="text-sm font-semibold text-primary">
            Everything HR, in one place
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground mt-3 mb-4">
            Built for the work HR actually does every day
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Not a bundle of disconnected tools wearing one login. One data
            model, shared across every module.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5">
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                className="group relative overflow-hidden bg-card border border-border rounded-2xl p-8 hover:border-primary/40 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-primary/5 group-hover:bg-primary/10 transition-colors duration-300" />
                <div className="relative w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <Icon size={22} className="text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="relative text-lg font-semibold text-foreground mb-2.5">
                  {feature.title}
                </h3>
                <p className="relative text-sm text-muted-foreground leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
